import * as React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Waves, Users, DollarSign, TrendingUp, Calendar, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface SurfSpotCardProps {
  className?: string;
  imageUrl: string;
  name: string;
  country: string;
  skillLevel: string;
  skillLabel?: string;
  waveType: string;
  crowdLevel: string;
  costRating: number;
  consistency: number;
  bestMonths: string[];
  vibe: string[];
  onExploreClick?: () => void;
}

// Compact skill-range formatter for the tight inner stat cell.
// Keeps the range legible ("Int–Adv") while fitting on one line at 375px.
const compactSkill = (s: string): string =>
  s
    .replace(/\bBeginner\b/gi, "Beg")
    .replace(/\bIntermediate\b/gi, "Int")
    .replace(/\bAdvanced\b/gi, "Adv")
    .replace(/\bExpert\b/gi, "Exp")
    .replace(/\s+(and|to)\s+/gi, "–");

const StatBox = ({
  label,
  value,
  icon: Icon,
  compact,
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  compact?: boolean;
}) => (
  <div className="flex flex-col items-center justify-center gap-0 p-0.5 sm:p-1 rounded bg-muted/50 border border-border/50 overflow-hidden">
    <Icon className="h-2 w-2 sm:h-2.5 sm:w-2.5 text-primary" />
    <span className="text-[9px] sm:text-[10px] font-semibold text-foreground leading-tight whitespace-nowrap">
      {compact && typeof value === "string" ? compactSkill(value) : value}
    </span>
    <span className="text-[7px] sm:text-[6px] uppercase tracking-wide text-muted-foreground whitespace-nowrap">{label}</span>
  </div>
);

const getCostDisplay = (rating: number): string => {
  return "$".repeat(rating);
};

const getConsistencyDisplay = (rating: number): string => {
  return `${rating}/10`;
};

const SurfSpotCard = React.forwardRef<HTMLDivElement, SurfSpotCardProps>(({
  className,
  imageUrl,
  name,
  country,
  skillLevel,
  skillLabel,
  waveType,
  crowdLevel,
  costRating,
  consistency,
  bestMonths,
  vibe,
  onExploreClick,
}, ref) => {
  const skillColors: Record<string, string> = {
    beginner: "bg-green-500/90",
    intermediate: "bg-yellow-500/90",
    advanced: "bg-orange-500/90",
    expert: "bg-red-500/90",
  };
  const displayLabel = skillLabel ?? (skillLevel.charAt(0).toUpperCase() + skillLevel.slice(1));

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
      className={cn(
        "w-[150px] sm:w-64 overflow-hidden rounded-xl sm:rounded-2xl bg-card border sm:border-2 border-primary/30 shadow-lg sm:shadow-2xl",
        className
      )}
    >
      {/* Top section with destination photography. This is meaningful product
          content, not decorative background -- no image-wide overlay. The
          name already has its own local contrast chip below, and the skill
          badge already has its own solid pill, so only a short gradient
          confined to the bottom band (behind the name) is needed. */}
      <div className="relative h-36 sm:h-44 overflow-hidden">
        <img
          src={imageUrl}
          alt={name}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/45 to-transparent" />

        {/* Skill level badge */}
        <div className={cn(
          "absolute top-1 right-1 sm:top-3 sm:right-3 px-1 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-[8px] sm:text-xs font-semibold text-white uppercase tracking-normal sm:tracking-wide whitespace-nowrap max-w-[calc(100%-8px)] truncate",
          skillColors[skillLevel] || "bg-primary/90"
        )}>
          {displayLabel}
        </div>

        {/* Name overlay at bottom-left of image */}
        <div className="absolute bottom-0 left-0 p-1 sm:p-4">
          <h3 className="text-[8px] sm:text-xl font-bold text-white leading-tight bg-card/80 backdrop-blur-sm px-1 py-0.5 sm:px-2 sm:py-1 rounded">
            {name}
          </h3>
        </div>
      </div>

      {/* Bottom section with stats grid */}
      <div className="p-1 sm:p-2 space-y-0.5 sm:space-y-1.5 bg-card">
        {/* Stats grid - 2x3 layout */}
        <div className="grid grid-cols-2 gap-0.5 sm:gap-1">
          <StatBox 
            icon={Sparkles}
            value={displayLabel} 
            label="Experience"
            compact
          />
          <StatBox 
            icon={Waves}
            value={waveType.charAt(0).toUpperCase() + waveType.slice(1)} 
            label="Wave Type" 
          />
          <StatBox 
            icon={Users}
            value={crowdLevel.charAt(0).toUpperCase() + crowdLevel.slice(1)} 
            label="Crowd" 
          />
          <StatBox 
            icon={DollarSign}
            value={getCostDisplay(costRating)} 
            label="Cost" 
          />
          <StatBox 
            icon={TrendingUp}
            value={getConsistencyDisplay(consistency)} 
            label="Consistency" 
          />
          <StatBox 
            icon={Calendar}
            value={bestMonths.slice(0, 2).join(", ")} 
            label="Best Months" 
          />
        </div>

        {/* Vibe row - hidden on mobile */}
        <div className="hidden sm:flex flex-col items-center gap-0 p-0.5 sm:p-1 rounded bg-muted/50 border border-border/50">
          <span className="text-[5px] sm:text-[6px] uppercase tracking-wide text-muted-foreground">Vibe</span>
          <span className="text-[9px] sm:text-[10px] font-medium text-foreground text-center leading-tight">
            {vibe.slice(0, 3).map(v => v.charAt(0).toUpperCase() + v.slice(1)).join(" • ")}
          </span>
        </div>

        {/* Explore button */}
        <Button 
          onClick={onExploreClick}
          className="w-full group text-[7px] sm:text-sm h-5 sm:h-9 px-1 sm:px-3"
          size="sm"
        >
          <span className="sm:hidden">View</span>
          <span className="hidden sm:inline">Explore Destination</span>
          <ArrowRight className="h-2 w-2 sm:h-4 sm:w-4 transition-transform group-hover:translate-x-1" />
        </Button>
      </div>
    </motion.div>
  );
});

SurfSpotCard.displayName = "SurfSpotCard";

export { SurfSpotCard };
export type { SurfSpotCardProps };
